import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { SKIP_ERROR_HANDLING } from '../interface/http-context.interface';

/**
 * Opciones base para todas las peticiones HTTP del wrapper
 */
export interface HttpOptions {
  /** Headers HTTP personalizados */
  headers?: HttpHeaders | Record<string, string | string[]>;
  /** Parámetros de query string */
  params?: HttpParams | Record<string, string | string[]>;
  /** Si se debe observar el cuerpo de la respuesta o la respuesta completa */
  observe?: 'body' | 'response';
  /** Reportar progreso de la petición */
  reportProgress?: boolean;
  /** Tipo de respuesta esperada */
  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  /** Incluir credenciales en la petición */
  withCredentials?: boolean;
  /**
   * Omite el manejo automático de errores del interceptor
   *
   * @remarks
   * Cuando es true, el InterceptorService NO mostrará el modal de error
   * automático. Esto permite que el servicio consumidor maneje el error
   * con lógica personalizada en su propio catchError.
   *
   * @default false
   *
   * @example
   * ```typescript
   * // El servicio maneja el error personalizado
   * this.http.get('/api/endpoint', { skipErrorHandling: true }).pipe(
   *   catchError((error) => {
   *     // Lógica personalizada de manejo de error
   *     this.showCustomToast(error.message);
   *     return of(null);
   *   })
   * );
   * ```
   */
  skipErrorHandling?: boolean;
}

/**
 * Wrapper del HttpClient que centraliza las llamadas HTTP
 *
 * @remarks
 * - Delega todas las operaciones al HttpClient nativo
 * - El InterceptorService maneja tokens, errores y headers automáticamente
 * - Proporciona una API tipada y consistente para todos los servicios
 * - Soporta skipErrorHandling para controlar el manejo automático de errores
 *
 * @example
 * ```typescript
 * // En un servicio
 * export class PersonasService {
 *   private readonly http = inject(HttpService);
 *
 *   getPersonas(): Observable<PersonaInterface[]> {
 *     return this.http.get<PersonaInterface[]>('/api/personas');
 *   }
 *
 *   // Con manejo de error personalizado
 *   getPersonaSilent(id: number): Observable<PersonaInterface | null> {
 *     return this.http.get<PersonaInterface>(`/api/personas/${id}`, {
 *       skipErrorHandling: true
 *     }).pipe(
 *       catchError(() => of(null))
 *     );
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly httpClient = inject(HttpClient);

  /**
   * Construye el HttpContext con las opciones personalizadas
   */
  private buildContext(skipErrorHandling: boolean): HttpContext | undefined {
    if (skipErrorHandling) {
      return new HttpContext().set(SKIP_ERROR_HANDLING, true);
    }
    return undefined;
  }

  /**
   * Construye las opciones finales para la petición HTTP
   */
  private buildOptions<T extends HttpOptions>(
    options: T | undefined
  ): Record<string, unknown> {
    if (!options) {
      return {};
    }

    const { skipErrorHandling, ...httpOptions } = options;
    const context = this.buildContext(skipErrorHandling ?? false);

    return {
      ...httpOptions,
      ...(context ? { context } : {}),
    };
  }

  // ==================== GET ====================

  /**
   * Realiza una petición HTTP GET
   *
   * @param url - URL del endpoint
   * @param options - Opciones opcionales (params, headers, skipErrorHandling, etc.)
   * @returns Observable con la respuesta tipada
   */
  get<T>(url: string, options?: HttpOptions): Observable<T>;

  /**
   * Realiza una petición HTTP GET con respuesta completa
   */
  get<T>(url: string, options: HttpOptions & { observe: 'response' }): Observable<HttpResponse<T>>;

  get<T>(url: string, options?: HttpOptions): Observable<T | HttpResponse<T>> {
    // debug: log outgoing GET request
    try {
      // eslint-disable-next-line no-console
    } catch (e) { }
    return this.httpClient.get<T>(url, this.buildOptions(options));
  }

  // ==================== POST ====================

  /**
   * Realiza una petición HTTP POST
   *
   * @param url - URL del endpoint
   * @param body - Cuerpo de la petición
   * @param options - Opciones opcionales (headers, skipErrorHandling, etc.)
   * @returns Observable con la respuesta tipada
   */
  post<T>(url: string, body?: unknown, options?: HttpOptions): Observable<T>;

  /**
   * Realiza una petición HTTP POST con respuesta completa
   */
  post<T>(url: string, body: unknown | null, options: HttpOptions & { observe: 'response' }): Observable<HttpResponse<T>>;

  post<T>(url: string, body?: unknown, options?: HttpOptions): Observable<T | HttpResponse<T>> {
    return this.httpClient.post<T>(url, body, this.buildOptions(options));
  }

  // ==================== PUT ====================

  /**
   * Realiza una petición HTTP PUT
   *
   * @param url - URL del endpoint
   * @param body - Cuerpo de la petición
   * @param options - Opciones opcionales (headers, skipErrorHandling, etc.)
   * @returns Observable con la respuesta tipada
   */
  put<T>(url: string, body?: unknown, options?: HttpOptions): Observable<T>;

  /**
   * Realiza una petición HTTP PUT con respuesta completa
   */
  put<T>(url: string, body: unknown | null, options: HttpOptions & { observe: 'response' }): Observable<HttpResponse<T>>;

  put<T>(url: string, body?: unknown, options?: HttpOptions): Observable<T | HttpResponse<T>> {
    return this.httpClient.put<T>(url, body, this.buildOptions(options));
  }

  // ==================== DELETE ====================

  /**
   * Realiza una petición HTTP DELETE
   *
   * @param url - URL del endpoint
   * @param options - Opciones opcionales (params, skipErrorHandling, etc.)
   * @returns Observable con la respuesta tipada
   */
  delete<T>(url: string, options?: HttpOptions): Observable<T>;

  /**
   * Realiza una petición HTTP DELETE con respuesta completa
   */
  delete<T>(url: string, options: HttpOptions & { observe: 'response' }): Observable<HttpResponse<T>>;

  delete<T>(url: string, options?: HttpOptions): Observable<T | HttpResponse<T>> {
    return this.httpClient.delete<T>(url, this.buildOptions(options));
  }

  // ==================== PATCH ====================

  /**
   * Realiza una petición HTTP PATCH
   *
   * @param url - URL del endpoint
   * @param body - Cuerpo de la petición con los cambios parciales
   * @param options - Opciones opcionales (headers, skipErrorHandling, etc.)
   * @returns Observable con la respuesta tipada
   */
  patch<T>(url: string, body?: unknown, options?: HttpOptions): Observable<T>;

  /**
   * Realiza una petición HTTP PATCH con respuesta completa
   */
  patch<T>(url: string, body: unknown | null, options: HttpOptions & { observe: 'response' }): Observable<HttpResponse<T>>;

  patch<T>(url: string, body?: unknown, options?: HttpOptions): Observable<T | HttpResponse<T>> {
    return this.httpClient.patch<T>(url, body, this.buildOptions(options));
  }
}
